import { Ionicons } from "@expo/vector-icons";
import Constants from "expo-constants";
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react";
import {
  Animated,
  Platform,
  ScrollView,
  StyleSheet,
  TouchableNativeFeedback,
  TouchableOpacity,
  TouchableWithoutFeedback,
  View,
} from "react-native";
import { Badge, Button, useTheme } from "react-native-elements";
import { Gesture, GestureDetector } from "react-native-gesture-handler";
import { getEndingData } from "../../backend/endings/Endings";
import {
  clearFlag,
  hasFlag,
  hasGlobalFlag,
  setFlag,
} from "../../backend/Flags";
import { isInventoryUnlocked } from "../../backend/items/Items";
import { getUnreadNotificationsByGroup } from "../../backend/Notifications";
import { PlayerContext } from "../../backend/PlayerContext";
import { getResourceAmount, Resource } from "../../backend/Resources";
import { getTotalElementAndSchoolExp } from "../../backend/spells/ElementsAndSchools";
import { getTemporaryEffects } from "../../backend/timetick/TemporaryEffects";
import {
  getCurrentTimePieceSpeedupCost,
  getCurrentTimePieceSpeedupSpeed,
} from "../../backend/timetick/TimePieces";
import { formatNumber } from "../../utils/FormattingUtils";
import CategoryContainer from "../utility/CategoryContainer";
import { PlayerCombatStatsViewer } from "../utility/CombatStatsViewer";
import { SchoolViewer } from "../utility/ElementViewer";
import { GameIcon } from "../utility/icons/GameIcon";
import { KeyboardListener } from "../utility/KeyboardListener";
import { Markdown } from "../utility/Markdown";
import { useGestureHandler, useLayoutType } from "../utility/Miscellaneous";
import { ResourceViewer } from "../utility/ResourceViewer";
import { TemporaryEffectsViewer } from "../utility/TemporaryEffectsViewer";
import { Text } from "../utility/Text";
import { VisibilityContext } from "../utility/VisibilityContext";

type Props = {
  onNavigationPress: (section: string) => void;
  onBackgroundPress: () => void;
  pinnedSections: string[];
  onPressFavorite: (section: string) => void;
  visible: boolean;
  onSwipeLeft: () => void;
  onSwipeRight: () => void;
};

const boldHotkeyLetter = (title: string, hotkey: string, theme: any) => {
  const hotkeyPos = title.indexOf(hotkey);
  let firstHalf = "";
  let lastHalf = "";

  if (hotkeyPos == 0) {
    lastHalf = title.substring(1);
  } else {
    firstHalf = title.substring(0, hotkeyPos);
    lastHalf = title.substring(hotkeyPos + 1);
  }

  return (
    <Text>
      {firstHalf}
      <Text
        style={[
          styles.hotkeyText,
          theme.theme.mode == "light" && { color: "#088" },
        ]}
      >
        {hotkey}
      </Text>
      {lastHalf}
    </Text>
  );
};

const MenuComponent = (
  props: {
    title: string;
    onPress: () => void;
    showBadge: boolean;
    showFavoriteIcon: boolean;
    favorited: boolean;
    onPressFavorite: () => void;
    hotkey?: string;
    children: React.ReactNode;
  } & any,
) => {
  const { title, onPress, showBadge, ...others } = props;
  const theme = useTheme();
  const visible = useContext(VisibilityContext);
  const onKeyboard = useCallback(
    (ev: KeyboardEvent) => {
      if (!visible || props.hotkey == null) {
        return;
      }

      if (
        ((!ev.shiftKey && !ev.ctrlKey) || (ev.shiftKey && ev.ctrlKey)) &&
        ev.key.toLowerCase() == props.hotkey.toLowerCase()
      ) {
        onPress();
      }
    },
    [visible, onPress],
  );
  const favoriteButton = useMemo(
    () => (
      <TouchableOpacity
        accessibilityLabel={props.favorited ? "Unpin Section" : "Pin Section"}
        onPress={props.onPressFavorite}
        style={styles.favoriteButton}
      >
        <Ionicons
          name={props.favorited ? "heart" : "heart-outline"}
          color={theme.theme.colors.black}
          size={14}
        />
      </TouchableOpacity>
    ),
    [props.favorited, props.onPressFavorite, theme],
  );
  return (
    <View>
      <KeyboardListener callback={onKeyboard} />
      <TouchableOpacity
        onPress={onPress}
        Component={Platform.select<any>({
          android: TouchableNativeFeedback,
          default: TouchableOpacity,
        })}
        accessibilityLabel={title}
        accessibilityRole="button"
        accessible={true}
        focusable={true}
        {...others}
        style={styles.menuComponentTouch}
      >
        <View style={styles.menuComponentButton}>
          <GameIcon
            /* @ts-expect-error accessibilityHidden is supported in web */
            focusable={false}
            accessibilityHidden={true}
            icon={title.toLowerCase()}
          />
          {showBadge && (
            <Badge containerStyle={styles.notificationBadge} status="error" />
          )}
          {props.showFavoriteIcon && favoriteButton}
          {Platform.OS === "web" && props.hotkey ? (
            <Text>{boldHotkeyLetter(title, props.hotkey, theme)}</Text>
          ) : (
            <Text>{title}</Text>
          )}
        </View>
      </TouchableOpacity>
    </View>
  );
};

export function MenuContents(props: {
  visible: boolean;
  pinnedSections: string[];
  onPressFavorite: (section: string) => void;
  onNavigationPress: (section: string) => void;
  onSwipeLeft: () => void;
  onSwipeRight: () => void;
}) {
  const playerContext = useContext(PlayerContext);
  const playerContextRef = useRef(playerContext);

  const showResearch = hasFlag(playerContext, "research_unlocked");
  const showAutocast = hasFlag(playerContext, "autocast_unlocked");
  const showInventory = isInventoryUnlocked(playerContext);
  const showExploration = hasFlag(playerContext, "exploration_unlocked");
  const showRetirement = getTotalElementAndSchoolExp(playerContext) >= 10000;
  const showCreation = hasGlobalFlag(playerContext, "newgameplus_unlocked");
  const showUpgrades = hasFlag(playerContext, "upgrades_unlocked");
  const showCreatures = hasFlag(playerContext, "creatures_unlocked");
  const showStrengthening =
    hasGlobalFlag(playerContext, "strengthening_unlocked") && showExploration;
  const showEnding = getEndingData(playerContext) != null;
  const showBestiary = hasGlobalFlag(playerContext, "bestiary_unlocked");

  const swipeHandlerGesture = useGestureHandler(
    props.onSwipeLeft,
    props.onSwipeRight,
  );
  const nativeGesture = useMemo(() => Gesture.Native(), []);
  const compositeGesture = useMemo(
    () => Gesture.Simultaneous(swipeHandlerGesture, nativeGesture),
    [swipeHandlerGesture, nativeGesture],
  );
  const layout = useLayoutType();

  const favoritesFull =
    props.pinnedSections.length >= layout.activeContentTabs - 1;

  const menuComponent = useCallback(
    (section: string, menuProps?: any) => (
      <MenuComponent
        title={section}
        key={section}
        onPress={() => props.onNavigationPress(section)}
        onPressFavorite={() => props.onPressFavorite(section)}
        favorited={props.pinnedSections.includes(section)}
        showFavoriteIcon={
          props.pinnedSections.includes(section) || !favoritesFull
        }
        {...(menuProps ?? {})}
      />
    ),
    [
      props.onNavigationPress,
      props.onPressFavorite,
      props.pinnedSections.join(),
      favoritesFull,
    ],
  );

  const helpNotifications = getUnreadNotificationsByGroup(
    playerContext,
    "Help",
  ).length;

  const navigationTiles = useMemo(
    () =>
      [
        menuComponent("Campus", { hotkey: "C" }),
        showResearch && menuComponent("Research", { hotkey: "R" }),
        showAutocast && menuComponent("Apprentices", { hotkey: "A" }),
        showInventory && menuComponent("Inventory", { hotkey: "I" }),
        showExploration && menuComponent("Exploration", { hotkey: "E" }),
        showUpgrades && menuComponent("Upgrades", { hotkey: "U" }),
        showCreatures && menuComponent("Creatures", { hotkey: "t" }),
        showStrengthening && menuComponent("Strengthen", { hotkey: "S" }),
        showEnding && menuComponent("Ending"),
        showCreation && menuComponent("Creation"),
        showRetirement && menuComponent("Retirement"),
        menuComponent("Stats"),
        showBestiary && menuComponent("Bestiary"),
        menuComponent("Help", { showBadge: helpNotifications > 0 }),
        menuComponent("Options"),
        menuComponent("About"),
        Constants?.expoConfig?.extra?.showDebug && menuComponent("Debug"),
      ].filter((value) => value),
    [
      showResearch,
      showAutocast,
      showInventory,
      showExploration,
      showUpgrades,
      showCreatures,
      showStrengthening,
      showEnding,
      showCreation,
      showRetirement,
      showBestiary,
      helpNotifications,
    ],
  );

  const navigationTileSet = useMemo(() => {
    let result = [];
    let list = [];

    for (let i = 0; i < navigationTiles.length; i++) {
      if (i > 0 && i % 3 == 0) {
        result.push(list);
        list = [];
      }

      list.push(navigationTiles[i]);
    }

    result.push(list);

    return result;
  }, [navigationTiles]);

  const theme = useTheme();

  const navigation = useMemo(
    () => (
      <View
        style={[
          styles.navigationColumn,
          { backgroundColor: theme.theme.colors.divider },
        ]}
      >
        {navigationTileSet.map((row, index) => {
          return (
            <View style={[styles.navigationRow]} key={`${index}-row`}>
              {row.map((tile, index) => {
                return (
                  <View
                    style={{
                      flex: 1,
                      backgroundColor: theme.theme.colors.background,
                      margin: StyleSheet.hairlineWidth,
                    }}
                    key={`${index}-tile`}
                  >
                    {tile}
                  </View>
                );
              })}
            </View>
          );
        })}
      </View>
    ),
    [navigationTileSet, theme],
  );

  const hasTemporaryEffects = getTemporaryEffects(playerContext).length > 0;

  const gameSpeed = getCurrentTimePieceSpeedupSpeed(playerContext);
  const hasTimePieces =
    getResourceAmount(playerContext, Resource.TimePieces) > 0;
  const speedupCost = getCurrentTimePieceSpeedupCost(playerContext);
  const timePieceCost = `${formatNumber(speedupCost)}:timepiece:/extra sec`;

  return useMemo(
    () => (
      <GestureDetector key="menuGesture" gesture={compositeGesture}>
        <ScrollView
          style={styles.scrollContainer}
          contentContainerStyle={styles.scrollContentContainer}
        >
          <View style={styles.titleContainer}>
            <Text h1>Magic Research</Text>
          </View>
          {navigation}
          <CategoryContainer title="Resources">
            <View style={styles.statViewer}>
              <ResourceViewer isVisible={props.visible} />
            </View>
          </CategoryContainer>
          <CategoryContainer title="Schools">
            <View style={styles.statViewer}>
              <SchoolViewer isVisible={props.visible} />
            </View>
          </CategoryContainer>
          {hasTimePieces && (
            <CategoryContainer title="Speed up time">
              <View style={styles.timePieceButtonsContainer}>
                <Text>Set game speed:</Text>
                <Button
                  title="Normal"
                  onPress={() =>
                    playerContextRef.current.apply((state) => {
                      state = clearFlag("time_piece_8x_speedup_enabled")(state);
                      state = clearFlag("time_piece_speedup_enabled")(state);
                      return state;
                    })
                  }
                  titleStyle={styles.timePieceButtonTitle}
                  containerStyle={[styles.timePieceButtonContainer]}
                />
                <Button
                  title="2x"
                  onPress={() =>
                    playerContextRef.current.apply((state) => {
                      state = clearFlag("time_piece_8x_speedup_enabled")(state);
                      state = setFlag("time_piece_speedup_enabled")(state);
                      return state;
                    })
                  }
                  titleStyle={styles.timePieceButtonTitle}
                  containerStyle={[styles.timePieceButtonContainer]}
                />
                <Button
                  title="8x"
                  onPress={() =>
                    playerContextRef.current.apply((state) => {
                      state = setFlag("time_piece_8x_speedup_enabled")(state);
                      state = clearFlag("time_piece_speedup_enabled")(state);
                      return state;
                    })
                  }
                  titleStyle={styles.timePieceButtonTitle}
                  containerStyle={[styles.timePieceButtonContainer]}
                />
              </View>
              <View style={styles.timePieceTextRows}>
                <Text>Current speed:</Text>
                <Text>{gameSpeed}x</Text>
              </View>
              {gameSpeed > 1 && (
                <View style={styles.timePieceTextRows}>
                  <View>
                    <Text>Time Piece cost:</Text>
                  </View>
                  <View>
                    <Markdown>{timePieceCost}</Markdown>
                  </View>
                </View>
              )}
            </CategoryContainer>
          )}
          {showExploration && (
            <CategoryContainer title="Combat">
              <View style={styles.statViewer}>
                <PlayerCombatStatsViewer isVisible={props.visible} />
              </View>
            </CategoryContainer>
          )}
          {hasTemporaryEffects && (
            <CategoryContainer title="Buffs">
              <TemporaryEffectsViewer isVisible={props.visible} />
            </CategoryContainer>
          )}
        </ScrollView>
      </GestureDetector>
    ),
    [
      navigation,
      showExploration,
      hasTemporaryEffects,
      hasTimePieces,
      gameSpeed,
      props.visible,
      compositeGesture,
    ],
  );
}

export function Menu(props: Props) {
  const positionAnim = useRef(
    new Animated.Value(props.visible ? 0 : -300),
  ).current;

  const opacityAnim = useRef(
    new Animated.Value(props.visible ? 0.5 : 0),
  ).current;

  useEffect(() => {
    if (props.visible) {
      Animated.timing(positionAnim, {
        toValue: 0,
        duration: 150,
        useNativeDriver: true,
      }).start();
      Animated.timing(opacityAnim, {
        toValue: 0.5,
        duration: 150,
        useNativeDriver: true,
      }).start();
    } else {
      Animated.timing(positionAnim, {
        toValue: -300,
        duration: 150,
        useNativeDriver: true,
      }).start();
      Animated.timing(opacityAnim, {
        toValue: 0,
        duration: 150,
        useNativeDriver: true,
      }).start();
    }
  }, [props.visible]);

  const theme = useTheme();

  return useMemo(
    () => (
      <>
        <TouchableWithoutFeedback
          accessibilityLabel="Close Menu"
          accessibilityRole="button"
          onPress={props.onBackgroundPress}
          accessibilityElementsHidden={true}
          importantForAccessibility="no-hide-descendants"
        >
          <Animated.View
            style={[styles.backdrop, { opacity: opacityAnim }]}
            pointerEvents={props.visible ? "auto" : "none"}
          />
        </TouchableWithoutFeedback>
        <Animated.View
          style={[
            styles.container,
            {
              transform: [{ translateX: positionAnim }],
              backgroundColor: theme.theme.colors.background,
            },
          ]}
          accessibilityElementsHidden={!props.visible}
          importantForAccessibility={
            !props.visible ? "no-hide-descendants" : "auto"
          }
        >
          <MenuContents {...props} />
        </Animated.View>
      </>
    ),
    [positionAnim, opacityAnim, props.onBackgroundPress, props.visible, theme],
  );
}

const styles = StyleSheet.create({
  backdrop: {
    position: "absolute",
    height: "100%",
    width: "100%",
    backgroundColor: "#000",
    zIndex: 130,
  },
  container: {
    justifyContent: "flex-start",
    alignItems: "stretch",
    position: "absolute",
    height: "100%",
    width: 300,
    left: 0,
    top: 0,
    zIndex: 140,
  },
  scrollContainer: {
    padding: 8,
  },
  scrollContentContainer: {
    paddingBottom: 16,
  },
  titleContainer: { marginBottom: 8 },
  navigationColumn: {
    flexDirection: "column",
    padding: StyleSheet.hairlineWidth,
    marginHorizontal: 2,
  },
  navigationRow: {
    flexDirection: "row",
  },
  timePieceButtonContainer: { marginLeft: 4 },
  timePieceButtonTitle: { fontSize: 12 },
  statViewer: {
    marginTop: 4,
  },
  timePieceButtonsContainer: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginTop: 8,
  },
  timePieceTextRows: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginTop: 4,
  },
  menuComponentTouch: {
    height: 60,
  },
  menuComponentButton: {
    alignItems: "center",
    justifyContent: "center",
    flex: 1,
  },
  favoriteButton: {
    position: "absolute",
    top: 4,
    left: 4,
  },
  notificationBadge: {
    position: "absolute",
    top: 4,
    right: 4,
  },
  hotkeyText: {
    color: "#8ff",
    fontWeight: "bold",
  },
});
